<template>
    <div>
        <v-row>
            <v-col>
                <v-btn
                    color="green"
                    class="white--text"
                    @click="showNewUniverseDialog = true"
                    :loading="submitting"
                    depressed
                >
                    <v-icon left>
                        fas fa-plus
                    </v-icon>
                    Nyt univers
                </v-btn>
            </v-col>
        </v-row>
        <v-row>
            <v-col cols="6" >
                <loader v-if="loading || !users" />
                <template v-else>
                    <v-card v-for="(universe, universeIndex) in courses" :key="universe._id" class="mb-4 course-list">
                        <v-card-title>
                            <v-row>
                                <v-col>
                                    {{ universe._id }}
                                </v-col>
                                <v-col class="text-right">
                                    <v-btn
                                        color="green"
                                        class="white--text"
                                        @click="newCourse.universe = universe._id; showNewCourseDialog = true"
                                        :loading="submitting"
                                        depressed
                                    >
                                        <v-icon left>
                                            fas fa-plus
                                        </v-icon>
                                        Nyt kursus
                                    </v-btn>
                                </v-col>
                            </v-row>
                        </v-card-title>
                        <v-card-text>
                            <v-expansion-panels v-model="selectedCourseIndex[universe._id]" style="position: relative;" flat>
                                <v-expansion-panel style="position: relative" v-for="(course, courseIndex) in universe.courses" :key="course._id">
                                    <v-expansion-panel-header>
                                        <v-row align="center" no-gutters>
                                            <v-col cols="1">
                                                <v-btn
                                                    icon
                                                    :disabled="courseIndex === 0"
                                                    @click.stop="incrementCourseOrder(course._id)"
                                                    :loading="submitting"
                                                >
                                                    <v-icon>
                                                        fa-arrow-up
                                                    </v-icon>
                                                </v-btn>
                                            </v-col>
                                            <v-col cols="1">
                                                <v-btn
                                                    icon
                                                    :disabled="courseIndex === universe.courses.length - 1" 
                                                    @click.stop="decrementCourseOrder(course._id)"
                                                    :loading="submitting"
                                                >
                                                    <v-icon>
                                                        fa-arrow-down
                                                    </v-icon>
                                                </v-btn>
                                            </v-col>
                                            <v-spacer></v-spacer>
                                            <v-col cols="9">
                                                {{ course.name }}
                                            </v-col>
                                        </v-row>
                                    </v-expansion-panel-header>
                                    <v-expansion-panel-content>
                                        <div class="d-flex justify-space-between mb-2">
                                            <v-btn depressed color="green" class="white--text mt-4" @click="handleClickNewLesson(course._id)" :loading="submitting">
                                                <v-icon left>
                                                    fas fa-plus
                                                </v-icon>
                                                Ny lektion
                                            </v-btn>
                                            <v-btn outlined color="red" class="white--text mt-4" @click="deleteCourse(course._id)" :loading="submitting">
                                                <v-icon left>
                                                    fas fa-trash-alt
                                                </v-icon>
                                                Slet kursus
                                            </v-btn>
                                        </div>
                                        <v-list>
                                            <v-list-item-group v-model="selectedLessonIndex[universe._id]" class="grey lighten-5">
                                                <v-list-item
                                                    v-for="(lesson, lessonIndex) in course.lessons"
                                                    :key="lesson._id"
                                                    :style="videoBorderStyle(lessonIndex)"
                                                    @click="setSelectedLesson(universeIndex, courseIndex, lessonIndex)"
                                                >
                                                    <v-list-item-title>
                                                        <v-row align="center">
                                                            <v-col cols="1">
                                                                <v-btn icon :disabled="lessonIndex === 0">
                                                                    <v-icon>
                                                                        fa-arrow-up
                                                                    </v-icon>
                                                                </v-btn>
                                                            </v-col>
                                                            <v-col cols="1">
                                                                <v-btn icon :disabled="lessonIndex === course.lessons.length - 1">
                                                                    <v-icon>
                                                                        fa-arrow-down
                                                                    </v-icon>
                                                                </v-btn>
                                                            </v-col>
                                                            <v-spacer />
                                                            <v-col cols="6" v-line-clamp="1">{{ lesson.name }}</v-col>
                                                            <v-col cols="3" class="text-right">
                                                                <span v-if="lesson.test && lesson.test.questions && lesson.test.questions.length > 0">{{ lesson.test.questions.length }}</span>
                                                                <span v-else class="red--text">Mangler test</span>
                                                            </v-col>
                                                        </v-row>
                                                    </v-list-item-title>
                                                </v-list-item>
                                            </v-list-item-group>
                                        </v-list>
                                    </v-expansion-panel-content>
                                </v-expansion-panel>
                            </v-expansion-panels>
                        </v-card-text>
                    </v-card>
                </template>
            </v-col>
            <v-col cols="6">
                <v-card>
                    <v-card-text v-if="selectedLesson && users">
                        <v-text-field label="Navn" v-model="selectedLesson.name" @blur="updateLessonName" />
                        <v-file-input label="Video" accept="video/*" v-model="selectedLesson.video" />
                        <v-row>
                            <v-col>
                                <v-btn outlined @click="showEditFilesDialog = true">
                                    <v-icon left>fa-file</v-icon>
                                    Filer
                                </v-btn>
                            </v-col>
                        </v-row>
                        <v-row>
                            <v-col>
                                <v-btn outlined @click="showEditChaptersDialog = true">
                                    <v-icon left>fa-align-left</v-icon>
                                    Kapitler
                                </v-btn>
                            </v-col>
                        </v-row>
                        <v-row>
                            <v-col>
                                <v-btn outlined @click="showEditTestDialog = true">
                                    <v-icon left>fa-spell-check</v-icon>
                                    Test
                                </v-btn>
                            </v-col>
                        </v-row>
                        <v-row>
                            <v-col>
                                <v-btn outlined @click="showEditAgentsDialog = true">
                                    <v-icon left>fa-users</v-icon>
                                    Agenturer
                                </v-btn>
                            </v-col>
                        </v-row>
                        <v-row>
                            <v-col>
                                <v-btn outlined @click="deleteLesson" color="red" :loading="submitting">
                                    <v-icon left>fas fa-trash-alt</v-icon>
                                    Slet lektion
                                </v-btn>
                            </v-col>
                        </v-row>
                    </v-card-text>
                    <v-card-text v-else>
                        Vælg en lektion til venstre for at redigere denne eller brug knapperne til at oprette et nyt kursus eller en ny lektion.
                    </v-card-text>
                </v-card>
            </v-col>
        </v-row>
        <v-dialog v-model="showEditFilesDialog" v-if="selectedLesson" width="600">
            <v-card>
                <v-card-title>Rediger filer til "{{ selectedLesson.name }}"</v-card-title>
                <v-divider class="my-2" />
                <v-card-text>
                    <v-form>
                        <v-row align="center">
                            <v-col cols="4">
                                <v-text-field label="Navn" v-model="newFile.name" />
                            </v-col>
                            <v-col cols="4">
                                <v-file-input label="Fil" v-model="newFile.file" truncate-length="10" />
                            </v-col>
                            <v-col cols="4" class="text-right">
                                <v-btn color="green" class="white--text" @click="createFile" :loading="submitting"> <v-icon left>fa-plus</v-icon> Upload fil</v-btn>
                            </v-col>
                        </v-row>
                    </v-form>
                    <v-list class="striped-list">
                        <v-list-item v-for="(file, index) in selectedLesson.files" :key="index">
                            <v-list-item-title>
                                <v-row align="center">
                                    <v-col v-line-clamp="1">
                                        {{ file.name }}
                                    </v-col>
                                    <v-col v-line-clamp="1">
                                        {{ file.filename }}
                                    </v-col>
                                </v-row>
                            </v-list-item-title>
                            <v-list-item-action>
                                <v-icon v-if="submitting" small color="red" class="fa-spin">
                                    fa-spinner
                                </v-icon>
                                <v-icon v-else color="red" @click.stop="deleteFile(file._id)">
                                    fas fa-trash-alt
                                </v-icon>
                            </v-list-item-action>
                        </v-list-item>
                    </v-list>
                </v-card-text>
            </v-card>
        </v-dialog>
        <v-dialog v-model="showEditChaptersDialog" v-if="selectedLesson" width="700">
            <v-card>
                <v-card-title>Rediger kapitler til "{{ selectedLesson.name }}"</v-card-title>
                <v-divider class="my-2" />
                <v-card-text>
                    <video-player  class="vjs-custom-skin"
                        ref="videoPlayer"
                        :options="playerOptions"
                    >
                    </video-player>
                    <v-row justify="space-between" align="center">
                        <v-col>
                            <v-text-field label="Kapitelnavn" v-model="newChapter.name" />
                        </v-col>
                        <v-col>
                            <v-btn color="green" class="white--text float-right" @click="createChapter()" :loading="submitting">
                                <v-icon left>fa-plus</v-icon>
                                Nyt kapitel
                            </v-btn>
                        </v-col>
                    </v-row>
                    <v-list class="striped-list">
                        <v-list-item
                            v-for="(chapter, index) in selectedLesson.chapters" :key="index"
                            @click="skipToTimestamp(chapter.timestamp)"
                        >
                            <v-list-item-title>
                                <v-row align="center">
                                    <v-col>
                                        {{ chapter.name }}
                                    </v-col>
                                    <v-col class="text-right">
                                        {{ chapter.timestamp | timestamp }}
                                    </v-col>
                                    <v-col cols="2" class="text-right">
                                        <v-icon v-if="submitting" small color="red" class="fa-spin">
                                            fa-spinner
                                        </v-icon>
                                        <v-icon v-else color="red" @click.stop="deleteChapter(index)">
                                            fas fa-trash-alt
                                        </v-icon>
                                    </v-col>
                                </v-row>
                            </v-list-item-title>
                        </v-list-item>
                    </v-list>
                </v-card-text>
            </v-card>
        </v-dialog>
        <v-dialog v-model="showEditTestDialog" v-if="selectedLesson" width="700">
            <v-card>
                <v-card-title>Rediger test til "{{ selectedLesson.name }}"</v-card-title>
                <v-divider class="my-2" />
                <v-card-text>
                    <v-row>
                        <v-col>
                            <v-text-field
                                outlined
                                hide-details
                                label="Beståelseskrav"
                                type="number"
                                v-model="selectedLesson.test.minimumRequired"
                                @change="updateMinimumRequired" />
                        </v-col>
                    </v-row>
                    <v-divider />
                    <v-row>
                        <v-col>
                            <v-textarea rows="1" auto-grow outlined label="Spørgsmålstekst" v-model="newTestQuestion" />
                            <v-btn color="green" class="white--text" @click="createTestQuestion" :loading="submitting">
                                <v-icon left>fa-plus</v-icon>
                                Nyt spørgsmål
                            </v-btn>
                        </v-col>
                    </v-row>
                    <template v-if="selectedLesson.test.questions.length > 0">
                        <v-row v-for="(question, questionIndex) in selectedLesson.test.questions" :key="questionIndex">
                            <v-col>
                                <v-divider />
                                <v-row align="center">
                                    <v-col>
                                        <h3>{{ `${questionIndex + 1}. ${question.question}` }}</h3>
                                    </v-col>
                                    <v-col class="text-right">
                                        <v-btn small outlined color="red" class="white--text" @click="deleteTestQuestion(questionIndex)" :loading="submitting">
                                            <v-icon left>
                                                fas fa-trash-alt
                                            </v-icon>
                                            Slet spørgsmål
                                        </v-btn>
                                    </v-col>
                                </v-row>
                                <v-radio-group
                                    v-if="question.options.length > 0"
                                    :value="typeof question.correctOption === 'string' ? question.options.findIndex(option => option._id === question.correctOption) : question.correctOption"
                                >
                                <span v-if="!(typeof question.correctOption === 'string')">mangler opdatering!</span>
                                    <v-row v-for="(option, optionIndex) in question.options" :key="optionIndex" align="center">
                                        <v-col>
                                            <v-radio
                                                @click="updateCorrectOption({ questionId: question._id, optionId: option._id })"
                                                :label="option.option"
                                            />
                                        </v-col>
                                    <v-col class="text-right">
                                        <v-icon v-if="submitting" small color="red" class="fa-spin">
                                            fa-spinner
                                        </v-icon>
                                        <v-icon v-else small color="red" @click="deleteTestQuestionOption(questionIndex, option, optionIndex)">
                                            fas fa-trash-alt
                                        </v-icon>
                                    </v-col>
                                    </v-row>
                                </v-radio-group>
                                <v-row v-else>
                                    <v-col>
                                        Der er ingen svarmuligheder til dette spørgsmål. Skriv en svarmulighed nedenfor og tryk på "Ny svarmulighed" for at tilføje en ny svarmulighed.
                                    </v-col>
                                </v-row>
                                <v-row align="center">
                                    <v-col>
                                        <v-textarea hide-details rows="1" auto-grow outlined label="Svarmulighed" v-model="question.newTestQuestionOption" />
                                    </v-col>
                                    <v-col class="text-right">
                                        <v-btn
                                            depressed
                                            color="green"
                                            class="white--text"
                                            @click="createTestQuestionOption(question.newTestQuestionOption, questionIndex)"
                                            :loading="submitting"
                                        >
                                            <v-icon left>fa-plus</v-icon>
                                            Ny svarmulighed
                                        </v-btn>
                                    </v-col>
                                </v-row>
                                <v-row>
                                    <v-col>
                                        <v-select hide-details label="Kapitel" v-model="question.chapter" :items="chapterSelect" @change="updateTestChapter(questionIndex)" />
                                    </v-col>
                                </v-row>
                            </v-col>
                        </v-row>
                    </template>
                    <v-row v-else>
                        <v-col>
                            Der er ingen test-spørgsmål til denne lektion. Skriv et spørgsmål ovenfor og tryk på "Nyt spørgsmål" for at tilføje et nyt test-spørgsmål.
                        </v-col>
                    </v-row>
                </v-card-text>
            </v-card>
        </v-dialog>
        <v-dialog v-model="showEditAgentsDialog" v-if="selectedLesson && users" width="500">
            <v-card>
                <v-card-title>Rediger agenturer for "{{ selectedLesson.name }}"</v-card-title>
                <v-card-text>
                    <v-subheader>
                        <v-row align="center" class="pr-2">
                            <v-col cols="9">
                                Navn
                            </v-col>
                            <v-col cols="1" class="text-center">
                                T
                            </v-col>
                            <v-col cols="1" class="text-center">
                                G
                            </v-col>
                            <v-col cols="1" class="text-center">
                                B                                
                            </v-col>
                        </v-row>
                    </v-subheader>
                    <v-list class="striped-list collapsed-inputs controlpanel-training-view-agents-list">
                        <v-list-item v-for="(user, index) in users" :key="index">
                            <v-list-item-content>
                                <v-list-item-title>
                                    <v-row no-gutters align="center">
                                        <v-col cols="9">
                                            {{ user.name }}
                                        </v-col>
                                        <v-col cols="1">
                                            <v-checkbox v-model="selectedLesson.agents[user.uniqueId].assigned" hide-details @change="updateLessonAgent(user.uniqueId)" />
                                        </v-col>
                                        <v-col cols="1">
                                            <v-checkbox v-model="selectedLesson.agents[user.uniqueId].completed" hide-details @change="updateLessonAgent(user.uniqueId)" />
                                        </v-col>
                                        <v-col cols="1" class="text-right">
                                            <v-checkbox v-model="selectedLesson.agents[user.uniqueId].passed" hide-details @change="updateLessonAgent(user.uniqueId)" />                             
                                        </v-col>
                                    </v-row>
                                </v-list-item-title>
                            </v-list-item-content>
                        </v-list-item>
                    </v-list>
                </v-card-text>
            </v-card>
        </v-dialog>
        <v-dialog v-model="showNewUniverseDialog" width="500">
            <v-card>
                <v-card-title>Nyt Univers</v-card-title>
                <v-divider />
                <v-card-text>
                    <v-text-field label="Navn" hide-details v-model="newUniverse"></v-text-field>
                </v-card-text>
                <v-divider />
                <v-card-actions class="justify-end">
                    <v-btn class="red--text" text @click="showNewUniverseDialog = false">Annuller</v-btn>
                    <v-btn class="green--text" text @click="courses.push({ newUniverse })" :loading="submitting">Opret</v-btn>
                </v-card-actions>
            </v-card>
        </v-dialog>
        <v-dialog v-model="showNewCourseDialog" width="500">
            <v-card>
                <v-card-title>Nyt kursus</v-card-title>
                <v-divider />
                <v-card-text>
                    <v-text-field label="Navn" hide-details v-model="newCourse.name"></v-text-field>
                </v-card-text>
                <v-divider />
                <v-card-actions class="justify-end">
                    <v-btn class="red--text" text @click="showNewCourseDialog = false">Annuller</v-btn>
                    <v-btn class="green--text" text @click="createCourse" :loading="submitting">Opret</v-btn>
                </v-card-actions>
            </v-card>
        </v-dialog>
        <v-dialog v-model="showNewLessonDialog" width="500">
            <v-card>
                <v-card-title>Ny lektion</v-card-title>
                <v-divider />
                <v-card-text>
                    <v-text-field label="Navn" hide-details v-model="newLesson.name"></v-text-field>
                        <v-file-input label="Video" accept="video/*" v-model="newLesson.video" />
                </v-card-text>
                <v-divider />
                <v-card-actions class="justify-end">
                    <v-btn class="red--text" text @click="showNewLessonDialog = false">Annuller</v-btn>
                    <v-btn class="green--text" text @click="createLesson" :loading="submitting">Opret</v-btn>
                </v-card-actions>
            </v-card>
        </v-dialog>
    </div>
</template>

<script>

import 'video.js/dist/video-js.css'

import { videoPlayer } from 'vue-video-player'
import FormData from 'form-data'

export default {
    components: {
        videoPlayer
    },
    data () {
        return {
            newFile: {
                file: null,
                name: null
            },
            videoUrl: null,
            loading: true,
            submitting: false,
            newCourse: {
                name: null,
                universe: null
            },
            newTestQuestion: null,
            newChapter: {},
            newLesson: {},
            showNewUniverseDialog: false,
            showNewCourseDialog: false,
            showNewLessonDialog: false,
            showEditFilesDialog: false,
            showEditChaptersDialog: false,
            showEditTestDialog: false,
            showEditAgentsDialog: false,
            selectedCourseIndex: {},
            selectedLessonIndex: {},
            selectedCourse: null,
            selectedLesson: null,
            courses: null,
            newUniverse: null
        }
    },
    computed: {
        trainingCourses () {
            return this.courses.filter(({ universe })=> universe === 'training')
        },
        academyCourses () {
            return this.courses.filter(({ universe })=> universe === 'academy')
        },
        selectedLessonId () {
            return this.selectedLesson._id
        },
        playerOptions () {
            return {
                fluid: true,
                autoplay: true,
                language: 'en',
                sources: [{
                    type: "video/mp4",
                    src: this.videoUrl
                }]
            }
        },
        chapterSelect () {
            return [
                { text: 'Intet', value: null },
                ...this.selectedLesson.chapters.map(chapter => {
                return { text: chapter.name, value: chapter._id }
            })]
        },
        users () {
            if (this.$store.state.main.users) {
                return [...this.$store.state.main.users.filter(user => user.active)]
            } else return null
        }
    },
    methods: {
        async incrementCourseOrder (courseId) {
            this.submitting = true
            const response = await this.$http().post(`controlpanel/training/courses/${courseId}/incrementOrder`)
            if (response.status === 200) {
                this.getData()
            }
            this.submitting = false
        },
        async decrementCourseOrder (courseId) {
            this.submitting = true
            const response = await this.$http().post(`controlpanel/training/courses/${courseId}/decrementOrder`)
            if (response.status === 200) {
                this.getData()
            }
            this.submitting = false
        },
        setSelectedLesson (universeIndex, courseIndex, lessonIndex) {
            this.courses[universeIndex].courses[courseIndex].lessons[lessonIndex] = { 
                ...this.courses[universeIndex].courses[courseIndex].lessons[lessonIndex],
                agents: {
                    ...Object.fromEntries(this.users.map(user => [user.uniqueId, { assigned: false, completed: false, passed: false }])),
                    ...this.courses[universeIndex].courses[courseIndex].lessons[lessonIndex].agents
                }
            }
            this.selectedCourse = this.courses[universeIndex].courses[courseIndex]
            this.selectedLesson = this.courses[universeIndex].courses[courseIndex].lessons[lessonIndex]
        },
        async deleteFile (fileId) {
            this.submitting = true
            const { status } = await this.$http().delete(`controlpanel/training/courses/${this.selectedCourse._id}/lessons/${this.selectedLesson._id}/files/${fileId}`)
            if (status === 200) this.selectedLesson.files.splice(this.selectedLesson.files.findIndex(file => file._id === fileId), 1)
            this.submitting = false
        },
        async createFile () {
            this.submitting = true
            const file = this.newFile.file
            const chunkSize = 4194304
            let response
            let filename = null
            for (let i = 0; i < file.size; i += chunkSize) {
                const formData = new FormData()

                const chunk = file.slice(i, i + chunkSize)

                formData.append('data', chunk)

                response = await this.$http().post(`controlpanel/training/courses/${this.selectedCourse._id}/lessons/${this.selectedLesson._id}/files`, formData, {
                    params: {
                        extension: file.name.split('.').reverse()[0],
                        name: this.newFile.name,
                        filename: filename,
                        lastChunk: (i + chunkSize >= file.size) || null
                    }
                })

                if (response.status === 202) {
                    filename = response.data
                } else if (response.status === 201) {
                    if (!this.selectedLesson.files) this.selectedLesson.files = []
                    this.selectedLesson.files.push(response.data)
                    this.newFile = { name: null, file: null }
                }
            }
            this.submitting = false
        },
        async updateLessonAgent (uniqueId) {
            await this.$http().patch(`controlpanel/training/courses/${this.selectedCourse._id}/lessons/${this.selectedLesson._id}/agents/${uniqueId}`, this.selectedLesson.agents[uniqueId])
        },
        async updateLessonName () {
            await this.$http().put(`controlpanel/training/courses/${this.selectedCourse._id}/lessons/${this.selectedLesson._id}/name`, {
                name: this.selectedLesson.name
            })
        },
        async updateCorrectOption ( { questionId, optionId } ) {
            this.submitting = true
            await this.$http().put(`controlpanel/training/courses/${this.selectedCourse._id}/lessons/${this.selectedLesson._id}/test/questions/${questionId}/correctOption`, optionId)
            this.submitting = false
        },
        async updateTestChapter (questionIndex) {
            this.submitting = true
            const question = this.selectedLesson.test.questions[questionIndex]
            await this.$http().put(`controlpanel/training/courses/${this.selectedCourse._id}/lessons/${this.selectedLesson._id}/test/questions/${question._id}/chapter`, this.selectedLesson.test.questions[questionIndex].chapter)
            this.submitting = false

        },
        async updateMinimumRequired () {
            this.submitting = true
            await this.$http().put(`controlpanel/training/courses/${this.selectedCourse._id}/lessons/${this.selectedLesson._id}/test/minimumRequired`, this.selectedLesson.test.minimumRequired)
            this.submitting = false

        },
        async createTestQuestionOption (option, questionIndex) {
            this.submitting = true
            const question = this.selectedLesson.test.questions[questionIndex]
            const response = await this.$http().post(`controlpanel/training/courses/${this.selectedCourse._id}/lessons/${this.selectedLesson._id}/test/questions/${question._id}/options`, option)
            if (response.status === 200) {
                question.options.push(response.data)
                question.newTestQuestionOption = null
                if (
                    question.correctOption === null
                    || typeof question.correctOption === 'undefined'
                    || !question.options.find(option => option._id === question.correctOption)
                ) {
                    question.correctOption = question.options.length ? question.options[0]._id : null
                    this.updateCorrectOption({ questionId: question._id, optionId: option._id })
                }
            }
            this.submitting = false

        },
        async deleteTestQuestionOption (questionIndex, option, optionIndex) {
            this.submitting = true
            const question = this.selectedLesson.test.questions[questionIndex]
            const response = await this.$http().delete(`controlpanel/training/courses/${this.selectedCourse._id}/lessons/${this.selectedLesson._id}/test/questions/${question._id}/options/${option._id}`)
            if (response.status === 200) {
                question.options.splice(optionIndex, 1)
                if (
                    question.correctOption === null
                    || typeof question.correctOption === 'undefined'
                    || !question.options.find(option => option._id === question.correctOption)
                ) {
                    question.correctOption = question.options.length ? question.options[0]._id : null
                    this.updateCorrectOption({ questionId: question._id, optionId: option._id })
                }
            }
            this.submitting = false
        },
        async createTestQuestion () {
            this.submitting = true
            const response = await this.$http().post(`controlpanel/training/courses/${this.selectedCourse._id}/lessons/${this.selectedLesson._id}/test/questions`, this.newTestQuestion)
            if (response.status === 200) {
                this.selectedLesson.test.questions.push(response.data)
                this.newTestQuestion = null
            }
            this.submitting = false

        },
        async deleteTestQuestion (questionIndex) {
            this.submitting = true
            const questionId = this.selectedLesson.test.questions[questionIndex]._id
            const response = await this.$http().delete(`controlpanel/training/courses/${this.selectedCourse._id}/lessons/${this.selectedLesson._id}/test/questions/${questionId}`)
            if (response.status === 200) {
                this.selectedLesson.test.questions.splice(questionIndex, 1)
            }
            this.submitting = false

        },
        skipToTimestamp (timestamp) {
            this.$refs.videoPlayer.player.currentTime(timestamp)
        },
        async createChapter () {
            this.submitting = true
            this.newChapter.timestamp = this.$refs.videoPlayer.player.currentTime()
            const response = await this.$http().post(`controlpanel/training/courses/${this.selectedCourse._id}/lessons/${this.selectedLesson._id}/chapters`, this.newChapter)
            if (response.status === 200) {
                this.selectedLesson.chapters.push(response.data)
            }
            this.submitting = false

        },
        async deleteChapter (chapterIndex) {
            this.submitting = true
            const response = await this.$http().delete(`controlpanel/training/courses/${this.selectedCourse._id}/lessons/${this.selectedLesson._id}/chapters/${this.selectedLesson.chapters[chapterIndex]._id}`)
            if (response.status === 200) {
                this.selectedLesson.chapters.splice(chapterIndex, 1)
            }
            this.submitting = false
        },
        handleClickNewLesson (courseId) {
            this.showNewLessonDialog = true
            this.newLesson = {
                courseId: courseId
            }
        },
        async createCourse () {
            this.submitting = true
            let response = await this.$http().post('controlpanel/training/courses', {
                name: this.newCourse.name,
                universe: this.newCourse.universe
            })
            if (response.status === 200) {
                this.getData()
                this.showNewCourseDialog = false
            }
            this.submitting = false
        },
        async deleteCourse (id) {
            this.submitting = true
            let response = await this.$http().delete(`controlpanel/training/courses/${id}`)
            if (response.status === 200) {
                this.getData()
            }
            this.submitting = false
        },
        async deleteLesson () {
            this.submitting = true
            let response = await this.$http().delete(`controlpanel/training/courses/${this.selectedCourse._id}/lessons/${this.selectedLesson._id}`)
            if (response.status === 200) {
                this.getData()
                this.selectedCourseIndex = {}
                this.selectedLessonIndex = {}
                this.selectedCourse = null
                this.selectedLesson = null
            }
            this.submitting = false
        },
        async createLesson () {
            this.submitting = true

            const file = this.newLesson.video

            const chunkSize = 4194304

            let response

            let filename = null

            for (let i = 0; i < file.size; i += chunkSize) {
                const formData = new FormData()

                const chunk = file.slice(i, i + chunkSize)

                formData.append('data', chunk)

                response = await this.$http().post(`controlpanel/training/courses/${this.newLesson.courseId}/lessons`, formData, {
                    params: {
                        name: this.newLesson.name,
                        extension: this.newLesson.video.type.split('/')[1],
                        filename: filename,
                        lastChunk: (i + chunkSize >= file.size) || null
                    }
                })

                if (response.status === 202) {
                    filename = response.data
                } else if (response.status === 201) {
                    this.getData()
                    this.showNewLessonDialog = false
                }

            }

            this.submitting = false
        },
        async getData () {
            this.loading = true
            const response = await this.$http().get('controlpanel/training/courses')
            this.courses = response.data
            this.loading = false
        },
        videoBorderStyle (index) {
            return index === 0
            ? 'border: 1px solid rgba(0, 0, 0, 0.12)'
            : 'border: 1px solid rgba(0, 0, 0, 0.12); border-top: 0;'
        },
        async getVideoUrl () {
            const response = await this.$http().get(`training/video/${this.selectedLesson.filename}`)
            this.videoUrl = response.data
        }
    },
    watch: {
        selectedLesson: function () {
            if (this.selectedLesson) this.getVideoUrl()
        }
    },
    created () {
        this.getData()
    }
}
</script>

<style lang="scss">
.controlpanel-training-view-agents-list {
    .v-input__slot {
        justify-content: center;
    }
    .v-input--selection-controls__input {
        margin-right: 0 !important;
    }
}
</style>